import {FC, useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components";
import {IconButton} from "@mui/material";
import {partial} from "lodash";
import {getCTAModalData, getIsOpenSavedAnswersModal, getUser} from "modules/selectors";
import {hideModal} from "modules/actions";
import {createConnextraScriptTag, getValueByLang, share} from "modules/utils";
import {IMAGES_URL} from "modules/constants";
import {LanguageType, ShareNet} from "modules/enums";
import {PrimaryExternalLink, SecondaryButton} from "components";
import {BaseModal} from "./BaseModal";
import {ButtonsBox, ModalIcon, ModalText, ModalTitle} from "./common";
import icon from "assets/images/icons/modal-success-icon.png";
import {ReactComponent as TWIcon} from "assets/images/icons/twitter.svg";
import {ReactComponent as FBIcon} from "assets/images/icons/facebook.svg";

const ShareIcons = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 12px;
	gap: 4px;

	svg {
		height: 24px;
		width: 24px;
	}
`;

const Image = styled.img`
	width: 100%;
	margin-bottom: 30px;
	margin-top: 10px;
`;

const getShareMessagesForLang = {
	[LanguageType.EN]: () => {
		return `I just made my CFL Preseason Futures Picks, come get in on the action!`;
	},
	[LanguageType.FR]: () => {
		return `Je viens de faire mes futurs choix de pré-saison de la LCF, venez participer à l'action!`;
	},
};

export const SavedAnswersModal: FC = () => {
	const {t, i18n} = useTranslation();
	const dispatch = useDispatch();
	const isOpen = useSelector(getIsOpenSavedAnswersModal);
	const modalData = useSelector(getCTAModalData);
	const lang = i18n.language;
	const user = useSelector(getUser);

	const message = getShareMessagesForLang[lang as LanguageType]();

	const closeHandler = useCallback(() => {
		dispatch(hideModal("savedAnswersModal"));
	}, [dispatch]);

	const shareHandler = (sc: ShareNet) => {
		share({
			sc,
			message,
			user_id: user?.id,
		});

		createConnextraScriptTag(
			`https://zz.connextra.com/dcs/tagController/tag/8ce716f59752/f2p_preseasonfutures_shareclick`
		);
	};

	if (!isOpen || !modalData) {
		return null;
	}

	const title = getValueByLang(modalData.title, lang);
	const desc = getValueByLang(modalData.message, lang);
	const link = modalData.link;
	const linkText = getValueByLang(modalData.linkText, lang);
	const image = getValueByLang(modalData.image, lang);

	return (
		<BaseModal className="full-screen grey" handleClose={closeHandler}>
			<ModalIcon src={icon} alt="Success" />
			<ModalTitle dangerouslySetInnerHTML={{__html: title}} />
			<ModalText dangerouslySetInnerHTML={{__html: desc}} />

			{image && <Image src={`${IMAGES_URL}${image}`} alt="" />}

			<ButtonsBox>
				<SecondaryButton onClick={closeHandler}>
					{t("modal.saved_picks.edit_picks")}
				</SecondaryButton>

				<PrimaryExternalLink href={link}>{linkText}</PrimaryExternalLink>
			</ButtonsBox>

			<ShareIcons>
				<IconButton onClick={partial(shareHandler, ShareNet.Twitter)}>
					<TWIcon />
				</IconButton>

				<IconButton onClick={partial(shareHandler, ShareNet.Facebook)}>
					<FBIcon />
				</IconButton>
			</ShareIcons>
		</BaseModal>
	);
};
